import {
  Box,
  Typography,
  FormControl,
  Stack,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  FormGroup,
  TextField,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { daysList, FormInputs } from "../constants";
import CustomDatePicker from "src/shared/components/custom-date-picker";
import CustomTimePicker from "src/shared/components/custom-time-picker";

const ZoneSchedule: React.FC = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormInputs>();

  return (
    <Box
      sx={{
        borderRadius: "10px",
        mt: 3,
        px: { xs: 1.5, md: 3 },
        py: { xs: 2, md: 2 },
        backgroundColor: "transparent",
      }}
      border={"1px solid #5B5B5B"}
    >
      <Typography component="span">Schedule</Typography>

      <Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap={2}
        >
          <Typography fontSize={"14px"}>Shift Hours:</Typography>
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <Typography fontSize={"14px"}>From</Typography>
            <Controller
              name={`scheduleStartTime`}
              control={control}
              render={({ field }) => (
                <CustomTimePicker
                  {...field}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: { width: "135px" },
                      error: !!errors?.scheduleStartTime,
                      helperText: errors?.scheduleStartTime?.message ?? "",
                    },
                  }}
                />
              )}
            />
          </Stack>
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <Typography my={1.5} fontSize={"14px"}>
              To
            </Typography>
            <Controller
              name={"scheduleEndTime"}
              control={control}
              render={({ field }) => (
                <CustomTimePicker
                  {...field}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: { width: "135px" },
                      error: !!errors.scheduleEndTime,
                      helperText: errors.scheduleEndTime?.message ?? "",
                    },
                  }}
                />
              )}
            />
          </Stack>
          {/* flexible hours */}
          <Stack direction={"row"} gap={1} alignItems={"center"} ml={2}>
            <Typography my={1.5} fontSize={"14px"}>
              Flexible Hours
            </Typography>
            <Controller
              name={"scheduleFlexibleHours"}
              control={control}
              render={({ field }) => (
                <TextField
                  type={"number"}
                  sx={{ width: "75px" }}
                  label=""
                  {...field}
                  error={!!errors.scheduleFlexibleHours}
                  helperText={errors.scheduleFlexibleHours?.message ?? ""}
                  fullWidth
                  size="small"
                  InputLabelProps={{
                    shrink: field.value !== undefined,
                  }}
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack mt={1} direction={"row"} alignItems={"center"} gap={3}>
          <Typography>Repeat</Typography>
          <Controller
            name={"scheduleDays"}
            control={control}
            render={({ field }) => {
              const handleChange = (event: any) => {
                const { value } = event.target;
                const newValue = field.value?.includes(value)
                  ? field.value.filter((v) => v !== value) // remove if already selected
                  : [...(field.value || []), value]; // add if not selected
                field.onChange(newValue);
              };

              const error = errors?.scheduleDays?.message;

              return (
                <FormControl
                  component="fieldset"
                  error={!!error}
                  variant="standard"
                >
                  <FormGroup row>
                    {daysList.map((day) => (
                      <FormControlLabel
                        key={day.value}
                        control={
                          <Checkbox
                            value={day.value}
                            checked={field.value?.includes(day.value) || false}
                            onChange={handleChange}
                            size="small"
                          />
                        }
                        label={day.label}
                      />
                    ))}
                  </FormGroup>
                  <FormHelperText id="my-helper-text">{error}</FormHelperText>
                </FormControl>
              );
            }}
          />
        </Stack>
        <Stack
          mt={1}
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap={2}
        >
          <Typography>Shift Duration</Typography>
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <Typography fontSize={"14px"}>From</Typography>
            <Controller
              name={"scheduleStartDate"}
              control={control}
              render={({ field }) => (
                <CustomDatePicker
                  {...field}
                  format={"dd/MM/yyyy"}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: { width: "145px", mr: 2 },
                      error: !!errors.scheduleStartDate,
                      helperText: errors.scheduleStartDate?.message ?? "",
                      inputProps: { readOnly: true },
                    },
                  }}
                  minDate={new Date()}
                />
              )}
            />
          </Stack>
          <Stack direction={"row"} alignItems={"center"} gap={3}>
            <Stack direction={"row"} gap={1} alignItems={"center"}>
              <Typography fontSize={"14px"}>To</Typography>
              <Controller
                name={"scheduleEndDate"}
                control={control}
                render={({ field }) => (
                  <CustomDatePicker
                    {...field}
                    format={"dd/MM/yyyy"}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: { width: "145px", mr: 2 },
                        error: !!errors.scheduleEndDate,
                        helperText: errors.scheduleEndDate?.message ?? "",
                        inputProps: { readOnly: true },
                      },
                    }}
                    minDate={new Date()}
                  />
                )}
              />
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default ZoneSchedule;
